import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  SimpleFormField
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { IRegistrationNotice, IRegistrationView } from '../types';
import {
  RIA_DOOR_TITLE,
  RIA_VS_TEAMS,
  anchorLabel,
  idTypeInputMode,
  idTypeLabel,
  idTypePlaceholder,
  missStatement,
  unplacedFailure,
  validationErrors
} from './registration-copy';
import { MessageList, QuietAction, StepHeading, slotMessage, useStepAction } from './step-parts';

/** The two names the anchor's refusals come back under, each drawn beside its control. */
const ANCHOR_FIELDS: ReadonlySet<string> = new Set(['value', 'idType']);

/**
 * `anchor` of a firm's or a team's run, signed in — the number, before anything else (survey-api's
 * roadmap D2: legacy asked for the password first and minted 10 362 logins that never filled
 * anything in). A person's anchors are the wireframes' pages 02 and 05 (`NumberAnchorStep`).
 *
 * **What happens to the number is the server's to say.** A first `not_found` comes back on
 * this same step with `lookup: 'not_found'`, which is the only reason the retry message
 * exists; a second one, an outage and a match all move the run elsewhere, and the page draws
 * wherever it went. Nothing here counts attempts or reads the registry's answer ahead of the
 * step: `attempts` rides on the view, and the rule that reads it is `ForNewParticipant`'s.
 * The retry message therefore promises nothing about the second miss either: FA and FSP carry
 * on without the number, a team goes on to its questions, and the view says which only once it
 * has happened.
 *
 * **A survey that cannot take a miss says why, and the miss never moves it** (SR-416's RIA door,
 * RL §4). The SEC not listing an RIA firm's number comes back on this step at every attempt with
 * `view.notice` — survey-api's `sec_not_registered`, the workbook's sentence, drawn verbatim
 * under words of ours — and the run waits here: for another number, or for the registrant to
 * take the team survey instead ("Register as a team instead", `…/redirection`), which ends the
 * run with nothing made. Under the sentence the page says who registers as a firm and who as a
 * team — this app's copy, which survey-api leaves to the client (RL §4) and no workbook writes
 * out (the block plan's Q12). Where a notice is sent the retry message is not: "submit it
 * again" would promise a second miss goes somewhere it does not.
 *
 * **A number's shape is the server's too.** An SEC number is the one type survey-api refuses
 * in any other shape, with a `ValidationProblem` on `value` that is drawn under the field like
 * any other; the field shows the shape beforehand and checks nothing.
 *
 * **The type is asked only where there is a choice.** `idTypes` is the survey's catalogue —
 * FSP alone declares two (an NPN, an insurance licence) — and a survey with one sends none,
 * so the server reads the number as its only anchor rather than being told what it already
 * knows.
 *
 * **A team may have no number to give** (`/no-anchor`): a lead advisor with no CRD is a real
 * case (TEAM-AC-003), and the run then goes to the gating questions with nothing looked up.
 * Offered on TEAMS alone, because survey-api refuses it for every other survey — a person's or
 * a firm's registration anchors on its number, which is then simply missing.
 *
 * **Back abandons the run** (`/abandonment`, R7). The run was opened by the chooser's answer
 * and carries its survey code, so choosing again means another run — and leaving this one
 * `in_progress` would put a registrant who merely changed their mind in the staff queue as
 * somebody who walked away.
 */
export function AnchorStep({
  view,
  onSubmit,
  onNoAnchor,
  onRedirect,
  onBack
}: {
  view: IRegistrationView;
  onSubmit: (value: string, idType: string | undefined) => Promise<void>;
  onNoAnchor: () => Promise<void>;
  onRedirect: () => Promise<void>;
  onBack: () => Promise<void>;
}) {
  const [value, setValue] = React.useState('');
  const [idType, setIdType] = React.useState(view.idTypes[0] ?? '');
  const { pending, error, run } = useStepAction();

  const team = view.survey === 'TEAMS';
  const choosesType = view.idTypes.length > 1;
  const problems = validationErrors(error);
  const failure = unplacedFailure(error, ANCHOR_FIELDS, 'Could not check that number.');
  const survey = view.survey ?? '';

  return (
    <>
      <StepHeading
        title={survey === 'RIA_FIRM' ? 'Register your firm' : 'Register your team'}
        lede={
          `We start with ${team ? 'your lead advisor’s CRD number' : 'your number'}. ` +
          'It is added to your account at the last step.'
        }
      />

      {view.notice ? (
        <NumberNotice
          notice={view.notice}
          pending={pending}
          onRedirect={() => void run(onRedirect)}
        />
      ) : (
        view.lookup === 'not_found' && (
          <Alert className="mb-5">
            <AlertTitle>Check that number</AlertTitle>
            <AlertDescription>
              {missStatement(survey)}. Check it and try once more — if it is right, submit
              it again.
            </AlertDescription>
          </Alert>
        )
      )}

      <form
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void run(() => onSubmit(value, choosesType ? idType : undefined));
        }}
      >
        {choosesType && (
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">Which number are you entering?</legend>
            {view.idTypes.map((type) => (
              <label key={type} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="registration-id-type"
                  value={type}
                  checked={idType === type}
                  onChange={() => setIdType(type)}
                  className="size-4 accent-[var(--color-sapphire)]"
                />
                {idTypeLabel(type)}
              </label>
            ))}
            {problems.idType && (
              <p className="text-xs text-destructive">{problems.idType.join(' ')}</p>
            )}
          </fieldset>
        )}

        <SimpleFormField
          label={anchorLabel(survey, idType)}
          htmlFor="registration-anchor"
          required
          error={slotMessage(problems.value)}
        >
          <Input
            id="registration-anchor"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            inputMode={idTypeInputMode(idType)}
            placeholder={idTypePlaceholder(idType)}
            autoComplete="off"
            aria-invalid={!!problems.value}
          />
          <MessageList messages={problems.value} />
        </SimpleFormField>

        {failure && (
          <p role="alert" className="text-sm font-medium text-destructive">
            {failure}
          </p>
        )}

        <Button type="submit" disabled={pending} className="w-full">
          {pending ? 'Checking…' : 'Continue'}
        </Button>

        {team && (
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            className="w-full"
            onClick={() => void run(onNoAnchor)}
          >
            Our lead advisor has no CRD number
          </Button>
        )}

        <QuietAction label="Back" disabled={pending} onClick={() => void run(onBack)} />
      </form>
    </>
  );
}

/**
 * Why the number cannot register here, as survey-api sent it, and the ways on. The sentence is
 * `notice.text`, verbatim; the title and, for `sec_not_registered`, the criteria and the press
 * are ours. A code this build does not know is drawn with its sentence and nothing it would have
 * to guess: no criteria, and no press, since a redirect is the one survey's that has one.
 */
export function NumberNotice({
  notice,
  pending,
  onRedirect
}: {
  notice: IRegistrationNotice;
  pending: boolean;
  onRedirect: () => void;
}) {
  const ria = notice.code === 'sec_not_registered';
  return (
    <Alert className="mb-5" data-notice={notice.code}>
      <AlertTitle>
        {ria ? RIA_DOOR_TITLE : 'This number cannot register here'}
      </AlertTitle>
      <AlertDescription>
        <p>{notice.text}</p>
        {ria && (
          <>
            <p className="mt-3">Who registers how:</p>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {RIA_VS_TEAMS.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <p className="mt-3">
              Check the number and enter it again if it is wrong, or register as a team.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              disabled={pending}
              onClick={onRedirect}
            >
              Register as a team instead
            </Button>
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}
